import { FC, useEffect, useState } from 'react';
import { Empty, List } from 'antd';
import { StyledListItemContent } from 'theme/styled';
import { round2Decimal } from '@medicusunion/front-utils';
import { PageHeader } from '@ant-design/pro-layout';
import { useSearchParams } from 'react-router-dom';
import { SharedFolder, fetchSharedFolders } from 'services/api/storage';

import FolderItem from './FolderItem';
import { Loading } from 'components/ui';
import FileItem from './FileItem';

interface IProps {
  folderId: string;
  patientId: string;
}

const FolderContent: FC<IProps> = ({ folderId, patientId }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [folder, setFolder] = useState<SharedFolder | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    fetchSharedFolders({ patient_id: patientId, folder_id: folderId })
      .then(data => setFolder(data[0] || null))
      .catch(() => setFolder(null))
      .finally(() => setLoading(false));
  }, [folderId, patientId]);

  const children = folder?.children || [];
  const files = folder?.files || [];

  const onBack = () => {
    const pageQuery = searchParams.get('page');
    const newPage = pageQuery?.replace(`/${folderId}`, '') as string;

    searchParams.set('page', newPage);
    setSearchParams(searchParams);
  };

  if (loading) {
    return <Loading />;
  }

  if (!folder) {
    return null;
  }

  return (
    <StyledListItemContent>
      <PageHeader
        style={{ marginBottom: 16 }}
        title={folder.name}
        onBack={onBack}
      />
      {!children.length && !files.length ? (
        <Empty />
      ) : (
        <>
          {!!children.length && (
            <List
              dataSource={children}
              renderItem={item => (
                <List.Item className="list-content-item">
                  <FolderItem
                    key={`folder-${item.id}`}
                    id={item.id}
                    name={item.name}
                  />
                </List.Item>
              )}
            />
          )}
          {!!files.length && (
            <List
              dataSource={files}
              renderItem={item => (
                <List.Item className="list-content-item">
                  <FileItem
                    key={`file-${item.id}`}
                    format={item.mime}
                    patientId={patientId}
                    size={round2Decimal((Number(item.size) || 0) / 1024 / 1024)}
                    name={item.name}
                  />
                </List.Item>
              )}
            />
          )}
        </>
      )}
    </StyledListItemContent>
  );
};

export default FolderContent;
